import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, addMonths, subMonths, startOfWeek, endOfWeek, isToday, isPast } from 'date-fns';

// Get all days in a month including padding days from previous/next month
export const getCalendarDays = (date) => {
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
};

// Get month name and year
export const getMonthYearLabel = (date) => {
  return format(date, 'MMMM yyyy');
};

// Get short month name
export const getShortMonthLabel = (date) => {
  return format(date, 'MMM');
};

// Get day of week labels
export const getDayLabels = () => {
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
};

// Check if a date is in the current month
export const isInCurrentMonth = (date, currentMonth) => {
  return isSameMonth(date, currentMonth);
};

// Check if a date is today
export const isCurrentDay = (date) => {
  return isToday(date);
};

// Check if a date is in the past
export const isPastDate = (date) => {
  return isPast(date);
};

// Get next month
export const getNextMonth = (date) => {
  return addMonths(date, 1);
};

// Get previous month
export const getPreviousMonth = (date) => {
  return subMonths(date, 1);
};

// Format date for display
export const formatDate = (date, formatString = 'MMM dd') => {
  return format(date, formatString);
};

// Format date for input fields
export const formatDateForInput = (date) => {
  return format(date, 'yyyy-MM-dd');
};

// Parse date string to Date object
export const parseDate = (dateString) => {
  return new Date(dateString);
};

// Get week number for a date
export const getWeekNumber = (date) => {
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
  const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
};

// Get quarter for a date
export const getQuarter = (date) => {
  return Math.ceil((date.getMonth() + 1) / 3);
};

// Check if date is weekend
export const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

// Check if date is weekday
export const isWeekday = (date) => {
  return !isWeekend(date);
};

// Get business days between two dates
export const getBusinessDays = (startDate, endDate) => {
  const days = [];
  let currentDate = new Date(startDate);
  
  while (currentDate <= endDate) {
    if (isWeekday(currentDate)) {
      days.push(new Date(currentDate));
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return days;
};

// Get next business day
export const getNextBusinessDay = (date) => {
  let nextDay = new Date(date);
  nextDay.setDate(nextDay.getDate() + 1);
  
  while (isWeekend(nextDay)) {
    nextDay.setDate(nextDay.getDate() + 1);
  }
  
  return nextDay;
};

// Get previous business day
export const getPreviousBusinessDay = (date) => {
  let prevDay = new Date(date);
  prevDay.setDate(prevDay.getDate() - 1);
  
  while (isWeekend(prevDay)) {
    prevDay.setDate(prevDay.getDate() - 1);
  }
  
  return prevDay;
};

// Generate date range for bulk operations
export const generateDateRange = (startDate, endDate) => {
  const dates = [];
  let currentDate = new Date(startDate);
  
  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return dates;
};

// Get month boundaries for a date
export const getMonthBoundaries = (date) => {
  return {
    start: startOfMonth(date),
    end: endOfMonth(date)
  };
};

// Get year boundaries for a date
export const getYearBoundaries = (date) => {
  return {
    start: new Date(date.getFullYear(), 0, 1),
    end: new Date(date.getFullYear(), 11, 31)
  };
};
